import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useSession } from '../session.tsx';
import { Card, CopyButton, ErrorState, Field, Loading, Modal, Tag, useAsync, useToast } from '../ui.tsx';
import { fmtDateTime, fmtNumber, relativeTime } from '../format.ts';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Proprietário', admin: 'Administrador', editor: 'Editor',
  analyst: 'Analista', viewer: 'Visualizador',
};

const ROLE_DESCRIPTION: Record<string, string> = {
  owner: 'Controle total, incluindo exclusão do workspace.',
  admin: 'Gerencia membros, integrações e exclusões.',
  editor: 'Cria e edita vídeos, CTAs e publica.',
  analyst: 'Vê relatórios e diagnósticos, sem editar.',
  viewer: 'Somente leitura da biblioteca.',
};

export function SettingsPage(): JSX.Element {
  const { workspace, workspaces, user, refresh, selectWorkspace } = useSession();
  const toast = useToast();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [newWorkspace, setNewWorkspace] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<{ title: string; url: string; hint: string } | null>(null);

  const isAdmin = workspace ? ['owner', 'admin'].includes(workspace.role) : false;

  const members = useAsync(
    () => (workspace ? api<{ members: any[] }>(`/api/workspaces/${workspace.id}/members`) : Promise.resolve({ members: [] })),
    [workspace?.id],
  );
  const invites = useAsync(
    () => (workspace && isAdmin && !workspace.is_demo
      ? api<{ invites: any[] }>(`/api/workspaces/${workspace.id}/invites`)
      : Promise.resolve({ invites: [] })),
    [workspace?.id, isAdmin],
  );
  const demo = useAsync(() => api<{ workspace: any; stats: { events: number; videos: number } | null }>('/api/demo'), [user?.id]);
  const audit = useAsync(
    () => (workspace && isAdmin ? api<{ logs: any[] }>(`/api/workspaces/${workspace.id}/audit`) : Promise.resolve({ logs: [] })),
    [workspace?.id, isAdmin],
  );

  async function invite(): Promise<void> {
    if (!workspace) return;
    try {
      await api(`/api/workspaces/${workspace.id}/members`, { body: { email: inviteEmail, role: inviteRole } });
      toast('Membro adicionado.', 'ok');
      setInviteEmail('');
      members.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao adicionar membro.', 'error');
    }
  }

  /** Gera o link de convite. Não há envio de e-mail: você copia e entrega. */
  async function createInviteLink(): Promise<void> {
    if (!workspace) return;
    try {
      const data = await api<{ url: string; role: string; expires_at: string }>(
        `/api/workspaces/${workspace.id}/invites`,
        { body: { role: inviteRole, note: inviteEmail ? `Para ${inviteEmail}` : '' } },
      );
      setGeneratedLink({
        title: 'Link de convite',
        url: data.url,
        hint: `Papel: ${ROLE_LABEL[data.role] ?? data.role}. Vale 7 dias e só pode ser usado uma vez. Envie por WhatsApp, Slack ou o canal que preferir.`,
      });
      setInviteEmail('');
      invites.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao gerar o convite.', 'error');
    }
  }

  async function revokeInvite(id: string): Promise<void> {
    if (!workspace) return;
    try {
      await api(`/api/workspaces/${workspace.id}/invites/${id}`, { method: 'DELETE' });
      toast('Convite cancelado.', 'ok');
      invites.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao cancelar.', 'error');
    }
  }

  /** Link de redefinição de senha para um membro que perdeu o acesso. */
  async function createResetLink(member: { user_id: string; name: string; email: string }): Promise<void> {
    if (!workspace) return;
    try {
      const data = await api<{ url: string }>(
        `/api/workspaces/${workspace.id}/members/${member.user_id}/reset-link`,
        { method: 'POST' },
      );
      setGeneratedLink({
        title: `Redefinir a senha de ${member.name}`,
        url: data.url,
        hint: 'Vale 1 hora e só pode ser usado uma vez. Entregue apenas à pessoa dona da conta.',
      });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao gerar o link.', 'error');
    }
  }

  async function createWorkspace(): Promise<void> {
    try {
      const data = await api<{ workspace: { id: string } }>('/api/workspaces', { body: { name: newWorkspace }, workspace: null });
      await refresh();
      selectWorkspace(data.workspace.id);
      setNewWorkspace('');
      toast('Workspace criado.', 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao criar workspace.', 'error');
    }
  }

  async function reseedDemo(): Promise<void> {
    setSeeding(true);
    try {
      await api('/api/demo/seed', { method: 'POST', workspace: null });
      toast('Dados de demonstração regenerados.', 'ok');
      demo.reload();
      await refresh();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao gerar dados de demonstração.', 'error');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="page-sub">Workspaces, permissões, modo demonstração e registro de auditoria.</p>
        </div>
      </div>

      <div className="grid cols-2">
        <Card title="Conta">
          <p style={{ margin: 0 }}><b>{user?.name}</b></p>
          <p className="card-sub">{user?.email}</p>
          <p className="card-sub">Conta criada em {user ? fmtDateTime(user.created_at) : '—'}</p>
        </Card>

        <Card title="Workspaces">
          {workspaces.map((item) => (
            <div key={item.id} className="row" style={{ justifyContent: 'space-between', padding: '6px 0' }}>
              <span className="row tight">
                <b>{item.name}</b>
                <Tag tone={item.is_demo ? 'info' : 'muted'}>{item.is_demo ? 'demonstração' : ROLE_LABEL[item.role]}</Tag>
              </span>
              {item.id !== workspace?.id && (
                <button type="button" className="btn ghost small" onClick={() => selectWorkspace(item.id)}>Ativar</button>
              )}
            </div>
          ))}
          <div className="row" style={{ alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <Field label="Novo workspace" id="s-ws">
                <input id="s-ws" value={newWorkspace} onChange={(e) => setNewWorkspace(e.target.value)} placeholder="Nome" />
              </Field>
            </div>
            <button type="button" className="btn" style={{ marginBottom: 14 }} disabled={newWorkspace.trim().length < 2} onClick={createWorkspace}>
              Criar
            </button>
          </div>
        </Card>
      </div>

      <div style={{ height: 18 }} />

      <Card title="Membros e permissões" subtitle={workspace?.is_demo ? 'O workspace de demonstração não aceita convites.' : undefined}>
        {members.loading && <Loading />}
        {members.error && <ErrorState message={members.error} onRetry={members.reload} />}
        {members.data && (
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead><tr><th>Membro</th><th>E-mail</th><th>Papel</th><th /></tr></thead>
              <tbody>
                {members.data.members.map((member) => (
                  <tr key={member.user_id}>
                    <td>{member.name}</td>
                    <td>{member.email}</td>
                    <td><Tag tone={member.role === 'owner' ? 'ok' : 'muted'}>{ROLE_LABEL[member.role] ?? member.role}</Tag></td>
                    <td style={{ textAlign: 'right' }}>
                      {isAdmin && !workspace?.is_demo && (
                        <button
                          type="button" className="btn ghost small" style={{ marginRight: 6 }}
                          onClick={() => createResetLink(member)}
                        >
                          Link de senha
                        </button>
                      )}
                      {isAdmin && member.user_id !== user?.id && member.role !== 'owner' && (
                        <button
                          type="button" className="btn ghost small"
                          onClick={async () => {
                            await api(`/api/workspaces/${workspace!.id}/members/${member.user_id}`, { method: 'DELETE' });
                            members.reload();
                          }}
                        >
                          Remover
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin && !workspace?.is_demo && (
          <>
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <Field
                  label="E-mail (opcional)"
                  id="s-invite"
                  hint="Preenchido, adiciona direto quem já tem conta. Vazio, gera só o link."
                >
                  <input id="s-invite" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                </Field>
              </div>
              <div style={{ flex: 1 }}>
                <Field label="Papel" id="s-role" hint={ROLE_DESCRIPTION[inviteRole]}>
                  <select id="s-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                    {['admin', 'editor', 'analyst', 'viewer'].map((role) => (
                      <option key={role} value={role}>{ROLE_LABEL[role]}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <button type="button" className="btn" style={{ marginBottom: 14 }} onClick={createInviteLink}>
                Gerar link de convite
              </button>
              <button type="button" className="btn secondary" style={{ marginBottom: 14 }} disabled={!inviteEmail} onClick={invite}>
                Adicionar direto
              </button>
            </div>

            {(invites.data?.invites ?? []).filter((entry) => !entry.used_at && !entry.revoked_at).length > 0 && (
              <div className="table-wrap" style={{ marginTop: 8 }}>
                <table>
                  <thead><tr><th>Convite pendente</th><th>Papel</th><th>Expira</th><th /></tr></thead>
                  <tbody>
                    {(invites.data?.invites ?? [])
                      .filter((entry) => !entry.used_at && !entry.revoked_at)
                      .map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.note || 'sem identificação'}</td>
                          <td><Tag tone="muted">{ROLE_LABEL[entry.role] ?? entry.role}</Tag></td>
                          <td>{relativeTime(entry.expires_at)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button type="button" className="btn ghost small" onClick={() => revokeInvite(entry.id)}>
                              Cancelar
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <div style={{ height: 18 }} />

      <Card
        title="Modo demonstração"
        subtitle="Workspace separado, marcado como demo, com dados fictícios. Nada dele se mistura aos dados reais."
        actions={
          <button type="button" className="btn secondary" disabled={seeding} onClick={reseedDemo}>
            {seeding ? <span className="spinner-inline" /> : null} Regenerar dados fictícios
          </button>
        }
      >
        {demo.loading && <Loading />}
        {demo.data?.workspace ? (
          <div className="row" style={{ gap: 26 }}>
            <div><p className="stat-label">Workspace</p><p style={{ margin: 0 }}>{demo.data.workspace.name}</p></div>
            <div><p className="stat-label">Vídeos</p><p style={{ margin: 0 }}>{fmtNumber(demo.data.stats?.videos ?? 0)}</p></div>
            <div><p className="stat-label">Eventos fictícios</p><p style={{ margin: 0 }}>{fmtNumber(demo.data.stats?.events ?? 0)}</p></div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-3)', margin: 0 }}>Nenhum workspace de demonstração. Use o botão acima para criar um.</p>
        )}
      </Card>

      <div style={{ height: 18 }} />
      <ChangePassword />

      {generatedLink && (
        <Modal
          title={generatedLink.title}
          onClose={() => setGeneratedLink(null)}
          footer={<button type="button" className="btn" onClick={() => setGeneratedLink(null)}>Fechar</button>}
        >
          <p style={{ marginTop: 0 }}>{generatedLink.hint}</p>
          <div className="code-block">{generatedLink.url}</div>
          <div className="row" style={{ marginTop: 12 }}>
            <CopyButton value={generatedLink.url} label="Copiar link" />
          </div>
          <div className="alert warn" style={{ marginTop: 14 }}>
            Este link não aparece de novo depois que você fechar esta janela. Copie antes de sair.
          </div>
        </Modal>
      )}

      {isAdmin && (
        <>
          <div style={{ height: 18 }} />
          <Card title="Auditoria" subtitle="Ações administrativas registradas neste workspace.">
            {audit.loading && <Loading />}
            {audit.data && audit.data.logs.length > 0 ? (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Quando</th><th>Ação</th><th>Alvo</th><th>IP</th></tr></thead>
                  <tbody>
                    {audit.data.logs.slice(0, 50).map((log) => (
                      <tr key={log.id}>
                        <td>{fmtDateTime(log.ts)}</td>
                        <td><code style={{ fontSize: 11.5 }}>{log.action}</code></td>
                        <td><code style={{ fontSize: 11.5 }}>{log.target || '—'}</code></td>
                        <td>{log.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--text-3)', margin: 0 }}>Nenhuma ação registrada ainda.</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}


/** Troca de senha da própria conta, sem depender de link. */
function ChangePassword(): JSX.Element {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/auth/password', { body: { currentPassword: current, newPassword: next }, workspace: null });
      setCurrent('');
      setNext('');
      toast('Senha alterada. As outras sessões foram encerradas.', 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao alterar a senha.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Sua senha" subtitle="Alterar a senha encerra as sessões abertas em outros aparelhos.">
      <form onSubmit={submit}>
        <div className="grid cols-2">
          <Field label="Senha atual" id="cp-cur">
            <input id="cp-cur" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
          </Field>
          <Field label="Nova senha" id="cp-new" hint="Mínimo de 8 caracteres, com letras e números.">
            <input id="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" />
          </Field>
        </div>
        <button type="submit" className="btn" disabled={busy || !current || next.length < 8}>
          {busy ? <span className="spinner-inline" /> : null} Alterar senha
        </button>
      </form>
    </Card>
  );
}
